import { types, applySnapshot, onSnapshot } from "mobx-state-tree";
import _ from "lodash";
import stopword from "stopword";
import Storage from "store";

import Book from "app/api/book";
import Contents from "app/api/contents";
import BookmarkStore from "app/state/stores/BookmarkStore";

const AppState = types
  .model({
    _selectedSection: types.array(types.union(types.string, types.integer)),
    _selectedSectionItem: types.array(types.union(types.string, types.integer)),
    menuBarContents: types.enumeration("MENU_BAR_CONTENTS", [
      "MAIN_MENU",
      "BOOKMARKS_MENU",
      "USER_IS_BOOKMARKING",
      "USER_IS_DELETING",
      "USER_IS_CLEARING",
      "USER_IS_ANNOTATING",
      "BOOKMARKING_MENU",
    ]),
    isFullscreen: types.boolean,
    isTOCMenuOpen: types.boolean,
    isBookmarksMenuOpen: types.boolean,
    isSearchResultsOpen: types.boolean,
    isUserSearching: types.boolean,
    bookmarkingSelectionPath: types.array(
      types.union(types.string, types.integer)
    ),
    bookmarkingSelectionContent: types.string,
    bookmarkingNote: types.string,
    bookmarks: BookmarkStore,
    bookmarkToDelete: types.array(types.union(types.string, types.integer)),
    searchTerm: types.string,
    searchResults: types.frozen(),
    searchStatus: types.enumeration("SEARCH_RESULTS_STATUS", [
      "SEARCH_RESULTS_PENDING",
      "SEARCH_RESULTS_FOUND",
      "SEARCH_RESULTS_EMPTY",
    ]),
  })
  .views((self) => ({
    get isSearchResultsPending() {
      return self.searchStatus === "SEARCH_RESULTS_PENDING";
    },
    get isSearchResultsFound() {
      return self.searchStatus === "SEARCH_RESULTS_FOUND";
    },
    get isSearchResultsEmpty() {
      return self.searchStatus === "SEARCH_RESULTS_EMPTY";
    },
    get isMainMenuBar() {
      return self.menuBarContents === "MAIN_MENU";
    },
    get isBookmarksMenuBar() {
      return self.menuBarContents === "BOOKMARKS_MENU";
    },
    get isBookmarkingMenuBar() {
      return self.menuBarContents === "BOOKMARKING_MENU";
    },
    get isUserBookmarking() {
      return self.menuBarContents === "USER_IS_BOOKMARKING";
    },
    get isUserAnnotating() {
      return self.menuBarContents === "USER_IS_ANNOTATING";
    },
    get isUserDeleting() {
      return self.menuBarContents === "USER_IS_DELETING";
    },
    get isUserClearing() {
      return self.menuBarContents === "USER_IS_CLEARING";
    },
    get selectedSectionItem() {
      return self._selectedSectionItem.slice();
    },
    get selectedSection() {
      return self._selectedSection.slice();
    },
    get hasSearchResults() {
      return !_.isEqual(self.searchResults, { matches: [] });
    },
  }))
  .actions((self) => ({
    setSearchStatus(content) {
      self.searchStatus = content;
    },
    setMenuBar(content) {
      self.menuBarContents = content;
    },
    toggleFullscreen() {
      self.isFullscreen = !self.isFullscreen;
    },
    toggleTOCMenu() {
      self.isBookmarksMenuOpen = false;
      self.isUserSearching = false;
      self.isSearchResultsOpen = false;
      self.isTOCMenuOpen = !self.isTOCMenuOpen;
    },
    toggleSearchResults() {
      self.isBookmarksMenuOpen = false;
      self.isTOCMenuOpen = false;
      self.isSearchResultsOpen = !self.isSearchResultsOpen;
      self.isUserSearching = !self.isUserSearching;
    },
    toggleBookmarksMenu() {
      self.isTOCMenuOpen = false;
      self.isUserSearching = false;
      self.isSearchResultsOpen = false;
      self.isBookmarksMenuOpen = !self.isBookmarksMenuOpen;
      if (!self.isBookmarksMenuBar) {
        self.setMenuBar("BOOKMARKS_MENU");
      } else {
        self.setMenuBar("MAIN_MENU");
      }
    },
    closeMenus() {
      self.isTOCMenuOpen = false;
      self.isBookmarksMenuOpen = false;
      self.isUserSearching = false;
      self.isSearchResultsOpen = false;
    },
    changeSectionItem(sectionItem) {
      //DEBUG:  console.log("IN AppState sectionItem " + sectionItem);
      self._selectedSection = _.take(sectionItem, 2);
      self._selectedSectionItem = sectionItem;
    },
    newBookmarkingSelection({ path, content }) {
      self.newBookmarkingSelectionPath(path);
      self.newBookmarkingSelectionContent(content);
    },
    newBookmarkingSelectionPath(path) {
      self.bookmarkingSelectionPath = path;
    },
    newBookmarkingSelectionContent(content) {
      self.bookmarkingSelectionContent = _.isArray(content)
        ? Contents.get(content)
        : content;
    },
    newBookmarkingNote(note) {
      self.bookmarkingNote = note;
    },
    resetBookmarkingSelection() {
      self.bookmarkingSelectionPath = [];
      self.bookmarkingSelectionContent = "";
      self.bookmarkingNote = "";
    },
    resetBookmarkingNote() {
      self.bookmarkingNote = "";
    },
    prepareToDeleteBookmark(path) {
      self.bookmarkToDelete = path;
    },
    deleteBookmark() {
      self.bookmarks.delete(self.bookmarkToDelete);
    },
    clearBookmarks() {
      self.bookmarks.clearBookmarks();
    },
    newSearchTerm(term) {
      self.searchResults = { matches: [] };
      term = stopword.removeStopwords(_.words(term)).join(" ");
      self.searchTerm = term;
    },
    newSearchResults(results) {
      self.searchResults = results;
    },
    changeSearchStatus(status) {
      self.searchStatus = status;
    },
  }));

let appState = AppState.create({
  _selectedSection: ["$", "cover"],
  _selectedSectionItem: ["$", "cover"],
  menuBarContents: "MAIN_MENU",
  isFullscreen: false,
  isTOCMenuOpen: false,
  isBookmarksMenuOpen: false,
  isSearchResultsOpen: false,
  isUserSearching: false,
  bookmarkingSelectionPath: [],
  bookmarkingSelectionContent: "",
  bookmarkingNote: "",
  bookmarks: { collection: [] },
  bookmarkToDelete: [],
  searchTerm: "",
  searchResults: { matches: [] },
  searchStatus: "SEARCH_RESULTS_FOUND",
});

onSnapshot(appState, (storedAppState) => {
  // fetch AppState except search-related stuff
  storedAppState = {
    ...storedAppState,
    ...{
      isSearchResultsOpen: false,
      isUserSearching: false,
      searchTerm: "",
      searchResults: { matches: [] },
    },
  };

  Storage.set(JSON.stringify(Book.metadata), JSON.stringify(storedAppState));
});

async function fetchFromStorage() {
  if (_.isEqual(process.env.NODE_ENV, "development")) {
    await Storage.get(JSON.stringify(Book.metadata));
  }
  // the function is executed automatically when the promise is constructed
  const storedAppState = await Storage.get(JSON.stringify(Book.metadata));
  if (storedAppState) {
    applySnapshot(appState, JSON.parse(storedAppState));
  }
  return appState;
}

export { fetchFromStorage };

export default appState;



// WEBPACK FOOTER //
// ./src/app/state/AppState.js